import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/helpers';
import { ThumbsDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ThumbDownProps {
  onThumbsDown: () => void;
  onSubmitComment: (comment?: string) => Promise<void>;
  isActive: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

export function ThumbDown({
  onThumbsDown,
  onSubmitComment,
  isActive,
  disabled = false,
  isLoading = false,
}: ThumbDownProps) {
  const { t } = useTranslation('prince');
  const { t: tCommon } = useTranslation('common');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSubmitComment = async (comment?: string) => {
    setIsSubmitting(true);
    try {
      await onSubmitComment(comment);
    } finally {
      setComment('');
      setIsDialogOpen(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    await handleSubmitComment();
  };

  const handlePopoverClose = async (open: boolean) => {
    setIsDialogOpen(open);

    if (!open && isDialogOpen && !isSubmitting) {
      // User closed the popover (clicked away or pressed escape)
      // Submit without comment
      await handleSubmitComment();
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (comment.trim()) {
        await handleSubmitComment(comment.trim());
      } else {
        handlePopoverClose(false);
      }
    }
  };

  return (
    <Popover open={isDialogOpen} onOpenChange={handlePopoverClose}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            onThumbsDown();
            setIsDialogOpen(true);
          }}
          className="h-7 w-7"
          data-testid="thumbs-down-button"
          disabled={disabled || isActive || isLoading}
        >
          <ThumbsDown
            data-testid={`thumbs-down-icon-${isActive ? 'active' : 'inactive'}`}
            className={cn(
              'h-4 w-4',
              isActive || isLoading ? 'text-neutral-700 fill-neutral-700' : 'text-neutral-400'
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" side="top" align="center">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none" data-testid="rating-comment-title">
              {t('messages.rating.comment.title')}
            </h4>
            <p className="text-sm text-muted-foreground" data-testid="rating-comment-description">
              {t('messages.rating.comment.description')}
            </p>
          </div>

          <Textarea
            placeholder={t('messages.rating.comment.placeholder')}
            value={comment}
            onChange={e => setComment(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[80px] resize-none"
            disabled={isSubmitting}
            autoFocus
            data-testid="rating-comment-textarea"
          />

          <div className="flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSubmitting}
              data-testid="rating-comment-cancel-button"
            >
              {tCommon('buttons.cancel')}
            </Button>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleSubmitComment(comment.trim())}
                disabled={!comment.trim() || isSubmitting}
                data-testid="rating-comment-submit-button"
              >
                {isSubmitting
                  ? t('messages.rating.comment.sending')
                  : t('messages.rating.comment.send')}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
